import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { Controller } from 'react-hook-form'

import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  RadioLabel,
  Typography
} from '@barracuda-internal/bds-core'
import { LinearProgress } from '@material-ui/core'

// V1 imports
import IconAlertInfo from 'assets/images/icons/icon_alert_info.svg'
import YesNoDialog from 'components/libs/dialog/yesNoDialog/YesNoDialog'
import ScheduleMatrix from 'components/libs/scheduler/ScheduleMatrix'
import { InboundScheduleType } from 'types/Settings'
import { useFormatMessage } from 'lib/localization'
import styles from 'components/pages/settings/quarantineNotification/QuarantineNotificationStyles'
import { useAppDispatch } from 'redux/toolkit/hooks'
import { setErrorSnackBar, setSuccessSnackBar } from 'redux/features/app/appSlice'

// V2 module imports
import { useForm, useFormState, useI18n } from 'v2/modules/lib'
import { IQuarantineNotificationForm } from './quarantineNotificationForm'

const BASE_I18N_KEY = 'ess.settings.quarantine_notification'
const ERROR_I18N_KEY = 'ess.app.error'

export interface QuarantineNotificationPageProps {
  settingsForm: IQuarantineNotificationForm
}

export const QuarantineNotificationPage: React.FC<QuarantineNotificationPageProps> = ({ settingsForm }) => {
  const dispatch = useAppDispatch()

  const canSetState = useRef(true)
  const { t } = useI18n()
  const form = useForm({
    initialData: {
      ...settingsForm.userSettingsForm.getInitialValues(),
      ...settingsForm.domainSettingsForm.getInitialValues()
    },
    load: async () => {
      // eslint-disable-next-line prefer-const
      let [domainFormData, userFormData] = await Promise.all([
        settingsForm.domainSettingsForm.load(),
        settingsForm.userSettingsForm.load()
      ])
      const userFormState = settingsForm.userSettingsForm.getState()
      const domainFormState = settingsForm.domainSettingsForm.getState()
      if (userFormState.isEffectiveQnsSettingsMissing) {
        // When user's effective QNS settings are missing (404 in QNS microservice)
        // The form will use the domain's effective settings
        userFormData = settingsForm.userSettingsForm.apply(
          userFormState.currentUserDbSettings,
          domainFormState.currentQnsSettings
        )
      }
      return { ...userFormData, ...domainFormData }
    },
    validate: async () => {
      // Not implemented yet
    },
    save: async (dirtyFormData: Record<string, any>) => {
      try {
        await settingsForm.userSettingsForm.save(dirtyFormData)
        if (!canSetState.current) {
          return
        }
        dispatch(setSuccessSnackBar({ message: 'success_changing_settings' }))
      } catch {
        if (!canSetState.current) {
          return
        }
        dispatch(setErrorSnackBar({ message: 'error_changing_settings' }))
      }
    },
    schema: settingsForm.schema
  })

  const classes = styles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatErrorMessage = useFormatMessage(ERROR_I18N_KEY)
  const [isConfirmDialogOpened, setIsConfirmDialogOpen] = useState<boolean>(false)
  const onResetSchedule = useCallback(() => {
    setIsConfirmDialogOpen(true)
  }, [])
  const onCloseConfirmDialog = useCallback(() => setIsConfirmDialogOpen(false), [])

  const onResetYesNoDialog = useCallback(async () => form.load(), [form])

  const formState = useFormState(form)

  const isInProgress = useMemo(
    () => formState.isLoading || !formState.isReady,
    [formState.isReady, formState.isLoading]
  )

  const isFailed = useMemo(() => formState.isFailed, [formState.isFailed])

  const isSaving = useMemo(() => formState.isSaving, [formState.isSaving])

  const isOverrideEnabled = useMemo(
    () => formState.values.isAllowSpecifyInterval === '1',
    [formState.values.isAllowSpecifyInterval]
  )

  const isUserFormDirty = useMemo(() => formState.isDirty, [formState.isDirty])

  const isUserQnsSettingsMissing = useMemo(
    () => settingsForm.userSettingsForm.isDirectQnsSettingsMissing,
    [settingsForm.userSettingsForm.isDirectQnsSettingsMissing]
  )

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  return useMemo(
    () => (
      <>
        <YesNoDialog
          title={formatMessage('reset_dialog.title')}
          text={formatMessage('reset_dialog.message')}
          open={isConfirmDialogOpened}
          onClose={onCloseConfirmDialog}
          onConfirm={onResetYesNoDialog}
        />
        <Card elevation={1} data-testid="root-card">
          <CardHeader
            title={
              <Typography className={classes.title} data-testid="title">
                {formatMessage('title')}
              </Typography>
            }
          />
          {isInProgress && <LinearProgress data-testid="progress" />}
          {!isInProgress && isFailed && (
            <Alert className={classes.alert} severity="error" data-testid="alert">
              {formatErrorMessage('failed_to_load_content')}
            </Alert>
          )}
          {!isInProgress && !isFailed && (
            <>
              {!isOverrideEnabled && (
                <Box className={classes.determinationBox} data-testid="banner">
                  <Grid container direction="row" wrap="nowrap" alignItems="flex-start">
                    <Grid item>
                      <img alt="" src={IconAlertInfo} className={classes.determinationIcon} />
                    </Grid>
                    <Grid item>
                      <Typography noWrap variant="subtitle2">
                        {formatMessage('allow_override')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <CardActions className={classes.actions}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={onResetSchedule}
                  disabled={
                    !isOverrideEnabled || !isUserFormDirty || isInProgress || settingsForm.userSettingsForm.isReadonly
                  }
                  data-testid="reset-button"
                >
                  {formatMessage('buttons.reset')}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={form.save}
                  disabled={
                    !isOverrideEnabled ||
                    !isUserFormDirty ||
                    isSaving ||
                    isInProgress ||
                    settingsForm.userSettingsForm.isReadonly
                  }
                  data-testid="save-button"
                  isLoading={isSaving}
                >
                  {formatMessage('buttons.save')}
                </Button>
              </CardActions>
              <CardContent data-testid="content">
                <div className={classes.radioButtonWrapper}>
                  <Typography variant="subtitle1" data-testid="subtitle">
                    {formatMessage('default_interval')}
                  </Typography>
                  <Controller
                    control={form.control}
                    name="defaultInterval"
                    render={({ field: { onChange, value }, fieldState }) => (
                      <>
                        <RadioGroup
                          name="defaultInterval"
                          value={value}
                          onChange={onChange}
                          row
                          data-testid="radio-group"
                        >
                          <RadioLabel
                            value={InboundScheduleType.never}
                            label={formatMessage('buttons.default_interval.never')}
                            disabled={
                              !isOverrideEnabled ||
                              isSaving ||
                              isUserQnsSettingsMissing ||
                              isInProgress ||
                              settingsForm.userSettingsForm.isReadonly
                            }
                            control={<Radio color="primary" data-testid="radio-never" />}
                          />
                          <RadioLabel
                            value={InboundScheduleType.custom}
                            label={formatMessage('buttons.default_interval.scheduled')}
                            disabled={
                              !isOverrideEnabled ||
                              isSaving ||
                              isUserQnsSettingsMissing ||
                              isInProgress ||
                              settingsForm.userSettingsForm.isReadonly
                            }
                            control={<Radio color="primary" data-testid="radio-custom" />}
                          />
                        </RadioGroup>
                        {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                      </>
                    )}
                  />
                </div>
                {formState.values.defaultInterval !== InboundScheduleType.never && (
                  <div className={classes.schedulerWrapper}>
                    <Typography variant="subtitle1" data-testid="schedule-subtitle">
                      {formatMessage('schedule_notification')}
                    </Typography>
                    <RadioGroup row>
                      <Controller
                        control={form.control}
                        name="customInboundSchedule"
                        render={({ field: { onChange, value }, fieldState }) => {
                          if (!value) {
                            return (
                              // eslint-disable-next-line react/jsx-no-useless-fragment
                              <>
                                {fieldState.error && (
                                  <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>
                                )}
                              </>
                            )
                          }
                          return (
                            <>
                              <ScheduleMatrix
                                isEnabled={
                                  isOverrideEnabled &&
                                  formState.values.defaultInterval === 'custom' &&
                                  !isSaving &&
                                  !isInProgress &&
                                  !settingsForm.userSettingsForm.isReadonly
                                }
                                customInboundSchedule={{ ...value }}
                                doUpdate={schedule => {
                                  onChange({ target: { value: schedule } })
                                }}
                              />
                              {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                            </>
                          )
                        }}
                      />
                    </RadioGroup>
                    <FormHelperText data-testid="helper-text" className={classes.scheduleNotificationSubTitle}>
                      {formatMessage('schedule_notification_sub_title')}
                    </FormHelperText>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </>
    ),
    [
      formatMessage,
      isConfirmDialogOpened,
      onCloseConfirmDialog,
      onResetYesNoDialog,
      classes.title,
      classes.alert,
      classes.determinationBox,
      classes.determinationIcon,
      classes.actions,
      classes.radioButtonWrapper,
      classes.schedulerWrapper,
      classes.scheduleNotificationSubTitle,
      isInProgress,
      isFailed,
      formatErrorMessage,
      isOverrideEnabled,
      onResetSchedule,
      isUserFormDirty,
      form.save,
      form.control,
      isSaving,
      formState.values.defaultInterval,
      isUserQnsSettingsMissing,
      t,
      settingsForm.userSettingsForm.isReadonly
    ]
  )
}
